import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { closestCenter } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Icon, IconButton, ScrollArea, GlassRegular } from '../../../components/common';
import type { PermissionValue, UserProfile } from '@/features/auth/types';
import { canAccessScriptEditor } from '@/features/show-structure/permissions';
import { WorkspaceId, ToolId, TOOLS, ToolConfig, getWorkspace, filterScriptGatedTools } from '../workspace-config';
import { cn } from '@/utils/cn';
import { useMotionConfig } from '@/hooks/useMotionConfig';
import { useSortableList } from '@/hooks/useSortableList';
import type { StatusIndicator } from '@/features/show-structure/hooks/useScriptJobStatus';
import { useAppStore } from '@/store/useAppStore';
import { resolveWorkspacePrimaryToolIds, sortToolsByLabel } from '../workspace-tool-order';

export interface WorkspaceSidebarProps {
    activeWorkspace: WorkspaceId | null;
    activeToolId: ToolId | null;
    isCollapsed: boolean;
    userPermissions: PermissionValue[];
    onWorkspaceChange: (workspaceId: WorkspaceId) => void;
    onToolSelect: (toolId: ToolId) => void;
    onToggleCollapse: () => void;
    onWorkspaceSelectorClick: () => void;
    /** Status indicator for tools with background jobs (e.g., script import) */
    toolStatusMap?: Partial<Record<ToolId, StatusIndicator>>;
    /** Number of unread notifications for the bell badge */
    unreadCount?: number;
    /** Toggle the notification tray open/closed */
    onNotificationsToggle?: () => void;
    projectTitle?: string;
    user?: UserProfile | null;
    cloudLabel?: string | null;
    reviewCount?: number;
    onAccountClick?: () => void;
    onLoginClick?: () => void;
    onReviewClick?: () => void;
    toggleIconSet?: 'panel' | 'menu';
}

export const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
    activeWorkspace,
    activeToolId,
    isCollapsed,
    userPermissions,
    onToolSelect,
    onToggleCollapse,
    onWorkspaceSelectorClick,
    toolStatusMap = {},
    unreadCount = 0,
    onNotificationsToggle,
    projectTitle,
    user,
    cloudLabel,
    reviewCount = 0,
    onAccountClick,
    onLoginClick,
    onReviewClick,
    toggleIconSet = 'panel',
}) => {
    const [showAllTools, setShowAllTools] = React.useState(false);
    const motionConfig = useMotionConfig();

    // Compute unread feed count internally to isolate re-renders to the sidebar
    const feedPosts = useAppStore(state => state.feedPosts || []);
    const lastViewedFeedAt = useAppStore(state => state.lastViewedFeedAt || 0);
    const workspaceToolOrderOverrides = useAppStore(state => state.ui.workspaceToolOrderOverrides);
    const reorderWorkspaceTool = useAppStore(state => state.metaActions.reorderWorkspaceTool);
    const cloudMember = useAppStore(state => state.cloud.member);
    const roles = useAppStore(state => state.admin.roles);
    const unreadFeedCount = useMemo(() =>
        feedPosts.filter(post => !post.deleted && post.createdAt > lastViewedFeedAt).length,
        [feedPosts, lastViewedFeedAt]
    );
    const canUseScriptEditor = useMemo(() => canAccessScriptEditor(cloudMember, roles), [cloudMember, roles]);
    const scriptToolAccess = useMemo(() => ({ canUseScriptEditor }), [canUseScriptEditor]);

    // Get current workspace config
    const currentWorkspace = activeWorkspace ? getWorkspace(activeWorkspace) ?? null : null;

    // Filter tools based on user permissions, preserving the order from toolIds
    const getAccessibleTools = (toolIds?: ToolId[]): ToolConfig[] => {
        // If toolIds provided, map them to tools in that order (preserves workspace primaryTools order)
        const toolsToFilter = toolIds 
            ? toolIds.map(id => TOOLS.find(t => t.id === id)).filter((t): t is ToolConfig => !!t)
            : TOOLS;

        return toolsToFilter.filter(tool => {
            if (!tool.requiredPermissions) return true;
            return tool.requiredPermissions.some(p => userPermissions.includes(p));
        });
    };

    // Read script text access flag for tool gating
    const scriptTextAccess = useAppStore(state => state.cloud.scriptTextAccess ?? false);

    const primaryToolIds = currentWorkspace
        ? resolveWorkspacePrimaryToolIds(currentWorkspace, workspaceToolOrderOverrides)
        : [];

    // Get primary tools for the current workspace (filtered by permissions + script gating)
    const primaryTools = currentWorkspace 
        ? filterScriptGatedTools(getAccessibleTools(primaryToolIds), scriptTextAccess, scriptToolAccess)
        : [];

    // Get all accessible tools (for "All Tools" section)
    const allAccessibleTools = sortToolsByLabel(filterScriptGatedTools(getAccessibleTools(), scriptTextAccess, scriptToolAccess));

    const sortablePrimaryTools = isCollapsed ? [] : primaryTools;
    const {
        sensors,
        handleDragStart,
        handleDragEnd,
        handleDragCancel,
        DndContext,
        SortableContext,
        strategy,
        itemIds,
    } = useSortableList(sortablePrimaryTools, {
        onReorder: (activeId: string, overId: string) => {
            if (!currentWorkspace) {
                return;
            }

            reorderWorkspaceTool(currentWorkspace.id, activeId as ToolId, overId as ToolId);
        },
    });

    // Feed at the top — only show if user has permission
    const feedTool = getAccessibleTools(['feed'] as ToolId[]).find(t => t.id === 'feed') ?? null;
    const settingsTool = TOOLS.find(t => t.id === 'settings');
    const shouldShowContextBlock = !isCollapsed && (
        projectTitle !== undefined
        || cloudLabel !== undefined
        || user !== undefined
        || Boolean(onAccountClick)
        || Boolean(onLoginClick)
        || reviewCount > 0
    );
    const accountLabel = user?.displayName || user?.email || 'Signed in';
    const collapsedToggleIcon = toggleIconSet === 'panel' ? 'left_panel_open' : 'menu';
    const expandedToggleIcon = toggleIconSet === 'panel' ? 'left_panel_close' : 'menu_open';
    const toggleIcon = isCollapsed ? collapsedToggleIcon : expandedToggleIcon;

    return (
        <GlassRegular
            as="aside"
            className={cn(
                'flex flex-col h-full w-full transition-all duration-200 print:hidden overflow-hidden',
            )}
        >
            {/* Logo + Collapse Toggle */}
            <div className={cn(
                'flex items-center border-b border-gray-200 dark:border-border-dark',
                isCollapsed ? 'flex-col gap-1 py-2' : 'justify-between px-3 py-2.5'
            )}>
                {/* Logo — wordmark when expanded, icon mark when collapsed */}
                {isCollapsed ? (
                    <>
                        <img src="/assets/on-book-pro-logo-light.svg" alt="On Book Pro" width={28} height={28} className="h-7 w-7 object-contain dark:hidden" />
                        <img src="/assets/on-book-pro-logo-dark.svg" alt="On Book Pro" width={28} height={28} className="h-7 w-7 object-contain hidden dark:block" />
                    </>
                ) : (
                    <>
                        <img src="/assets/on-book-pro-wordmark-light.svg" alt="On Book Pro" width={149} height={24} className="h-6 object-contain dark:hidden" />
                        <img src="/assets/on-book-pro-wordmark-dark.svg" alt="On Book Pro" width={149} height={24} className="h-6 object-contain hidden dark:block" />
                    </>
                )}
                <IconButton
                    onClick={onToggleCollapse}
                    className={cn(
                        'flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-white/5 transition-colors',
                        isCollapsed ? 'h-8 w-8' : 'h-7 w-7'
                    )}
                    icon={toggleIcon}
                    label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    size="sm"
                    variant="ghost"
                />
            </div>

            {shouldShowContextBlock ? (
                <div className="border-b border-gray-200 px-3 py-3 dark:border-border-dark">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                        {projectTitle || 'Untitled Show'}
                    </p>
                    {cloudLabel ? (
                        <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                            {cloudLabel}
                        </p>
                    ) : null}
                    {user ? (
                        <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                            {accountLabel}
                        </p>
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-2">
                        {user ? (
                            <Button
                                className="min-h-[44px] px-3 text-sm"
                                onClick={onAccountClick}
                                size="md"
                                variant="secondary"
                            >
                                Account
                            </Button>
                        ) : (
                            <Button
                                className="min-h-[44px] px-3 text-sm"
                                onClick={onLoginClick}
                                size="md"
                                variant="primary"
                            >
                                Login
                            </Button>
                        )}
                        {reviewCount > 0 ? (
                            <Button
                                className="min-h-[44px] px-3 text-sm"
                                leftIcon="rule"
                                onClick={onReviewClick}
                                size="md"
                                variant="danger-subtle"
                            >
                                Review {reviewCount > 99 ? '99+' : reviewCount}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* Feed (Global) */}
            {feedTool ? <div className="border-b border-gray-200 dark:border-border-dark">
                    <SidebarTool
                        tool={feedTool}
                        isActive={activeToolId === 'feed'}
                        isCollapsed={isCollapsed}
                        onClick={() => onToolSelect('feed')}
                        badge={unreadFeedCount}
                    />
                </div> : null}

            {/* Workspace Selector */}
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        className="px-3 py-2 border-b border-gray-200 dark:border-border-dark overflow-hidden"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={motionConfig.subtle}
                    >
                        <Button
                            onClick={onWorkspaceSelectorClick}
                            className="min-h-0 w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 shadow-none hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                            size="sm"
                            variant="ghost"
                        >
                            <div className="flex items-center gap-2">
                                <Icon name={currentWorkspace?.icon || 'apps'} />
                                <span className="truncate">{currentWorkspace?.label || 'All Tools'}</span>
                            </div>
                            <Icon name="expand_more" />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Primary Tools */}
            <ScrollArea>
                {!isCollapsed && primaryTools.length > 0 && (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <SortableContext items={itemIds} strategy={strategy}>
                            <div className="py-2" data-testid="sidebar-primary-tools">
                                {primaryTools.map(tool => (
                                    <SortableSidebarTool
                                        key={tool.id}
                                        tool={tool}
                                        isActive={activeToolId === tool.id}
                                        onClick={() => onToolSelect(tool.id)}
                                        statusIndicator={toolStatusMap[tool.id]}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}

                {isCollapsed ? <div className="py-2" data-testid="sidebar-primary-tools">
                        {primaryTools.map(tool => (
                            <SidebarTool
                                key={tool.id}
                                tool={tool}
                                isActive={activeToolId === tool.id}
                                isCollapsed={isCollapsed}
                                onClick={() => onToolSelect(tool.id)}
                                statusIndicator={toolStatusMap[tool.id]}
                                testId={`sidebar-primary-tool-${tool.id}`}
                                dataToolId={tool.id}
                            />
                        ))}
                    </div> : null}

                {/* All Tools Section (Collapsible) */}
                {!isCollapsed && (
                    <div className="border-t border-gray-200 dark:border-border-dark">
                        <Button
                            onClick={() => setShowAllTools(!showAllTools)}
                            className="min-h-0 w-full items-center justify-between px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-none hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-white/5"
                            size="sm"
                            variant="ghost"
                        >
                            <span>All Tools</span>
                            <Icon name="expand_more" size="sm" className={cn('transition-transform', showAllTools && 'rotate-180')} />
                        </Button>
                        <AnimatePresence initial={false}>
                            {showAllTools ? <motion.div
                                    className="py-2 overflow-hidden"
                                    data-testid="sidebar-all-tools-list"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={motionConfig.subtle}
                                >
                                    {allAccessibleTools.map(tool => (
                                        <SidebarTool
                                            key={tool.id}
                                            tool={tool}
                                            isActive={activeToolId === tool.id}
                                            isCollapsed={isCollapsed}
                                            onClick={() => onToolSelect(tool.id)}
                                            statusIndicator={toolStatusMap[tool.id]}
                                            testId={`sidebar-all-tool-${tool.id}`}
                                            dataToolLabel={tool.label}
                                        />
                                    ))}
                                </motion.div> : null}
                        </AnimatePresence>
                    </div>
                )}
            </ScrollArea>

            {/* Utility Zone (Bottom) */}
            <div className="border-t border-gray-200 dark:border-border-dark">
                {/* Help/Documentation Link */}
                <a
                    href="https://docs.on-book.app/pro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                        'w-full flex items-center gap-3 px-6 py-2.5 transition-colors',
                        isCollapsed && 'justify-center px-0',
                        'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                    )}
                    title={isCollapsed ? 'Help & Documentation' : undefined}
                >
                    <Icon name="help" className="text-gray-500 dark:text-gray-400" />
                    {!isCollapsed && (
                        <span className="text-sm font-medium truncate flex-1 text-left">Help</span>
                    )}
                </a>

                {/* Notifications */}
                {onNotificationsToggle ? <Button
                        onClick={onNotificationsToggle}
                        className={cn(
                            'min-h-0 w-full items-center justify-start gap-3 px-6 py-2.5 shadow-none transition-colors',
                            isCollapsed && 'justify-center px-0',
                            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                        )}
                        size="sm"
                        title={isCollapsed ? 'Notifications' : undefined}
                        variant="ghost"
                    >
                        <div className="relative">
                            <Icon name="notifications" className="text-gray-500 dark:text-gray-400" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </div>
                        {!isCollapsed && (
                            <span className="text-sm font-medium truncate flex-1 text-left">Notifications</span>
                        )}
                    </Button> : null}

                {/* Settings */}
                {settingsTool ? <SidebarTool
                        tool={settingsTool}
                        isActive={activeToolId === 'settings'}
                        isCollapsed={isCollapsed}
                        onClick={() => onToolSelect('settings')}
                    /> : null}
            </div>
        </GlassRegular>
    );
};

/**
 * Individual sidebar tool button
 */
interface SidebarToolProps {
    tool: ToolConfig;
    isActive: boolean;
    isCollapsed: boolean;
    onClick: () => void;
    badge?: number;
    /** Status indicator dot: processing (blue pulse), warning (amber), success (green) */
    statusIndicator?: StatusIndicator;
    dragHandle?: React.ReactNode;
    testId?: string;
    dataToolId?: ToolId;
    dataToolLabel?: string;
}

const STATUS_DOT_STYLES: Record<NonNullable<StatusIndicator>, string> = {
    processing: 'bg-blue-500 animate-pulse',
    warning: 'bg-amber-500',
    success: 'bg-emerald-500',
};

const STATUS_INDICATOR_LABELS: Record<NonNullable<StatusIndicator>, string> = {
    processing: 'processing status',
    warning: 'warning status',
    success: 'success status',
};

interface SortableSidebarToolProps {
    tool: ToolConfig;
    isActive: boolean;
    onClick: () => void;
    statusIndicator?: StatusIndicator;
}

const SortableSidebarTool: React.FC<SortableSidebarToolProps> = ({ tool, isActive, onClick, statusIndicator }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: tool.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.72 : 1,
        position: 'relative',
        zIndex: isDragging ? 20 : 1,
    };

    const dragHandle = (
        <IconButton
            {...attributes}
            {...listeners}
            className="drag-handle touch-none cursor-grab text-gray-400 active:cursor-grabbing dark:text-gray-500"
            data-testid={`sidebar-tool-drag-handle-${tool.id}`}
            icon="drag_handle"
            label={`Reorder ${tool.label}`}
            size="sm"
            variant="ghost"
        />
    );

    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`sidebar-primary-tool-${tool.id}`}
            data-tool-id={tool.id}
        >
            <SidebarTool
                tool={tool}
                isActive={isActive}
                isCollapsed={false}
                onClick={onClick}
                statusIndicator={statusIndicator}
                dragHandle={dragHandle}
            />
        </div>
    );
};

const SidebarTool: React.FC<SidebarToolProps> = ({
    tool,
    isActive,
    isCollapsed,
    onClick,
    badge,
    statusIndicator,
    dragHandle,
    testId,
    dataToolId,
    dataToolLabel,
}) => {
    // Status dot takes precedence over numeric badge dot (when collapsed)
    const showStatusDot = statusIndicator && statusIndicator in STATUS_DOT_STYLES;
    const showBadgeDot = !showStatusDot && badge !== undefined && badge > 0;
    const toolActionLabel = [
        tool.label,
        showStatusDot ? STATUS_INDICATOR_LABELS[statusIndicator] : null,
        showBadgeDot ? `${badge} unread` : null,
    ].filter(Boolean).join(', ');
    const icon = (
        <div className="relative">
            <Icon name={tool.icon} className={cn(isActive ? 'text-primary' : 'text-gray-500 dark:text-gray-400')} />
            {/* Status indicator dot (collapsed mode) */}
            {showStatusDot && isCollapsed ? <span
                    aria-hidden="true"
                    className={cn(
                        'absolute -top-0.5 -right-0.5 block h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-surface-dark',
                        STATUS_DOT_STYLES[statusIndicator]
                    )}
                /> : null}
            {/* Unread badge dot (collapsed mode, fallback) */}
            {showBadgeDot && isCollapsed ? <span
                    aria-hidden="true"
                    className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-surface-dark"
                /> : null}
        </div>
    );
    const expandedMeta = !isCollapsed ? (
        <>
            <span className="text-sm font-medium truncate flex-1 text-left">{tool.label}</span>
            {/* Status indicator dot (expanded mode) */}
            {showStatusDot ? <span
                    aria-hidden="true"
                    className={cn(
                        'block h-2 w-2 rounded-full',
                        STATUS_DOT_STYLES[statusIndicator]
                    )}
                /> : null}
            {/* Unread badge pill (expanded mode, fallback) */}
            {!showStatusDot && badge !== undefined && badge > 0 && (
                <span
                    className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full"
                >
                    {badge > 99 ? '99+' : badge}
                </span>
            )}
        </>
    ) : null;

    if (dragHandle) {
        return (
            <div
                className={cn(
                    'group w-full flex items-center transition-colors relative',
                    isActive
                        ? 'bg-primary/10 text-primary dark:bg-primary/20'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                )}
                data-testid={testId}
                data-tool-id={dataToolId}
                data-tool-label={dataToolLabel}
            >
                {isActive ? <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" /> : null}
                <div className="flex items-center pl-3 pr-1">
                    {dragHandle}
                </div>
                <Button
                    onClick={onClick}
                    className="min-h-0 flex min-w-0 flex-1 items-center justify-start gap-3 rounded-none bg-transparent py-2.5 pl-0 pr-6 text-left text-sm text-current shadow-none hover:bg-transparent hover:text-current dark:text-current dark:hover:bg-transparent dark:hover:text-current"
                    size="sm"
                    variant="ghost"
                    aria-label={toolActionLabel}
                >
                    {icon}
                    {expandedMeta}
                </Button>
            </div>
        );
    }

    return (
        <Button
            onClick={onClick}
            className={cn(
                'relative min-h-0 w-full items-center justify-start gap-3 rounded-none bg-transparent px-6 py-2.5 text-sm shadow-none hover:text-current',
                isCollapsed && 'justify-center px-0',
                isActive
                    ? 'bg-primary/10 text-primary hover:bg-primary/10 dark:bg-primary/20 dark:hover:bg-primary/20'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
            )}
            size="sm"
            title={isCollapsed ? toolActionLabel : undefined}
            variant="ghost"
            aria-label={toolActionLabel}
            data-testid={testId}
            data-tool-id={dataToolId}
            data-tool-label={dataToolLabel}
        >
            {isActive ? <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" /> : null}
            {icon}
            {expandedMeta}
        </Button>
    );
};









